import React, { useCallback, useRef, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from 'reactstrap';
import { Html5Qrcode } from 'html5-qrcode';

const SCANNER_ELEMENT_ID = 'bags-barcode-scanner-region';

/**
 * Camera-based barcode / QR scan for tablets (e.g. iPad). Hardware scanners can keep using the text field.
 */
export const BarcodeScannerModal = ({ isOpen, toggle, onScan, title = 'Scan barcode or QR' }) => {
  const onScanRef = useRef(onScan);
  const toggleRef = useRef(toggle);
  const isOpenRef = useRef(isOpen);
  onScanRef.current = onScan;
  toggleRef.current = toggle;
  isOpenRef.current = isOpen;

  const scannerRef = useRef(null);
  const cancelledRef = useRef(false);

  const [starting, setStarting] = useState(false);
  const [hint, setHint] = useState('');

  const stopScannerInstance = useCallback(async () => {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    if (!scanner) {
      return;
    }
    try {
      await scanner.stop();
    } catch (_) {
      /* not scanning */
    }
    try {
      await scanner.clear();
    } catch (_) {
      /* ignore */
    }
  }, []);

  const handleModalClosed = useCallback(() => {
    cancelledRef.current = true;
    stopScannerInstance();
    setHint('');
    setStarting(false);
  }, [stopScannerInstance]);

  const handleModalOpened = useCallback(() => {
    if (!isOpenRef.current) {
      return;
    }
    cancelledRef.current = false;

    const scanConfig = {
      fps: 10,
      qrbox: { width: 280, height: 200 },
      aspectRatio: 1.777778,
    };

    const onDecoded = (decodedText) => {
      const text = (decodedText || '').trim();
      if (!text || cancelledRef.current) {
        return;
      }
      cancelledRef.current = true;
      onScanRef.current(text);
      stopScannerInstance().finally(() => {
        toggleRef.current();
      });
    };

    const run = async () => {
      if (!isOpenRef.current || cancelledRef.current) {
        return;
      }
      if (!document.getElementById(SCANNER_ELEMENT_ID)) {
        setHint('Scanner view is not ready. Close and open the scanner again.');
        return;
      }
      if (cancelledRef.current) {
        return;
      }

      setStarting(true);
      setHint('');

      let scanner;
      try {
        scanner = new Html5Qrcode(SCANNER_ELEMENT_ID);
      } catch (err) {
        setHint(err?.message || 'Could not initialize the scanner.');
        setStarting(false);
        return;
      }

      if (cancelledRef.current) {
        setStarting(false);
        return;
      }

      scannerRef.current = scanner;

      const cameraConfigs = [{ facingMode: 'environment' }, { facingMode: 'user' }];
      let lastErr = null;

      try {
        for (const cameraConfig of cameraConfigs) {
          if (cancelledRef.current) {
            break;
          }
          try {
            // eslint-disable-next-line no-await-in-loop
            await scanner.start(cameraConfig, scanConfig, onDecoded, () => {});
            lastErr = null;
            break;
          } catch (err) {
            lastErr = err;
            try {
              await scanner.stop();
            } catch (_) {
              /* ignore */
            }
          }
        }
        if (lastErr && !cancelledRef.current) {
          setHint(
            lastErr?.message ||
              'Could not start the camera. Allow camera access in Safari settings for this site.'
          );
        }
      } finally {
        if (!cancelledRef.current) {
          setStarting(false);
        }
      }
    };

    run();
  }, [stopScannerInstance]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      size="md"
      backdrop="static"
      onOpened={handleModalOpened}
      onClosed={handleModalClosed}
    >
      <ModalHeader toggle={toggle}>{title}</ModalHeader>
      <ModalBody className="position-relative">
        {starting ? (
          <div
            className="position-absolute w-100 h-100 d-flex flex-column align-items-center justify-content-center bg-white"
            style={{ zIndex: 2, top: 0, left: 0, minHeight: 260 }}
          >
            <Spinner color="primary" />
            <p className="mt-2 mb-0 text-muted">Starting camera…</p>
          </div>
        ) : null}
        <div id={SCANNER_ELEMENT_ID} className="w-100" style={{ minHeight: 260 }} />
        {hint ? <p className="text-danger small mt-2 mb-0">{hint}</p> : null}
        <p className="text-muted small mt-2 mb-0">
          Point the camera at a barcode or QR code. The search field updates when a code is read.
        </p>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};
